import React, { useState, useEffect, useCallback } from "react";

interface TimeDurationInputsProps {
  onChange?: (formattedTime: string) => void;
  value?: string; // Optional prop to set initial value (format: "HHH:MM")
}

export const TimeDurationInputs: React.FC<TimeDurationInputsProps> = ({ onChange, value }) => {
  // Initialize from value prop if provided (format: "HHH:MM")
  const parseInitialValue = useCallback((val?: string) => {
    if (!val || typeof val !== 'string') return { hours: "", minutes: "" };
    if (!val.includes(":")) return { hours: "", minutes: "" };
    const [h, m] = val.split(":");
    const hoursStr = h ? String(parseInt(h) || 0) : "";
    const minutesStr = m ? String(parseInt(m) || 0) : "";
    return {
      hours: hoursStr,
      minutes: minutesStr,
    };
  }, []);

  const initialValue = parseInitialValue(value);
  const [hours, setHours] = useState(initialValue.hours);
  const [minutes, setMinutes] = useState(initialValue.minutes);

  // Update hours and minutes when value prop changes
  useEffect(() => {
    if (value && typeof value === 'string' && value.includes(':')) {
      const parsed = parseInitialValue(value);
      setHours(parsed.hours);
      setMinutes(parsed.minutes);
    }
  }, [value, parseInitialValue]);

  const formatTime = (h: string, m: string) => {
    const hoursNum = parseInt(h) || 0;
    const minutesNum = parseInt(m) || 0;
    const formattedHours = hoursNum.toString().padStart(3, "0");
    const formattedMinutes = minutesNum.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes}`;
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (/^\d{0,3}$/.test(inputValue)) {
      const numValue = parseInt(inputValue) || 0;
      if (inputValue === "" || numValue <= 999) {
        setHours(inputValue);
      }
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (/^\d{0,2}$/.test(inputValue)) {
      if (inputValue === "") {
        setMinutes(inputValue);
        return;
      }

      const numValue = parseInt(inputValue, 10);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
        setMinutes(inputValue);
      }
    }
  };

  const handleHoursBlur = () => {
    if (hours === "") {
      setHours("0");
      onChange?.(formatTime("0", minutes));
    } else {
      onChange?.(formatTime(hours, minutes));
    }
  };

  const handleMinutesBlur = () => {
    if (minutes === "") {
      setMinutes("0");
      onChange?.(formatTime(hours, "0"));
    } else {
      const numValue = parseInt(minutes, 10);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
        onChange?.(formatTime(hours, minutes));
      } else {
        setMinutes("0");
        onChange?.(formatTime(hours, "0"));
      }
    }
  };

  const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].includes(e.keyCode) ||
      (e.keyCode === 65 && e.ctrlKey) ||
      (e.keyCode === 67 && e.ctrlKey) ||
      (e.keyCode === 86 && e.ctrlKey) ||
      (e.keyCode === 88 && e.ctrlKey)
    ) {
      return;
    }
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].includes(e.keyCode) ||
      (e.keyCode === 65 && e.ctrlKey) ||
      (e.keyCode === 67 && e.ctrlKey) ||
      (e.keyCode === 86 && e.ctrlKey) ||
      (e.keyCode === 88 && e.ctrlKey)
    ) {
      return;
    }
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Hours Input */}
      <div className="flex flex-col">
        <input
          id="hours"
          type="text"
          value={hours}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          onKeyDown={handleHoursKeyDown}
          placeholder="000"
          maxLength={3}
          className="w-16 px-2 py-2 text-sm text-center text-gray-600 border border-gray-300 rounded bg-bits-grey-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <label className="text-xs text-gray-500 text-center mt-1">Hours</label>
      </div>

      {/* Separator */}
      <div className="text-lg font-semibold text-gray-400 mb-4">:</div>

      {/* Minutes Input */}
      <div className="flex flex-col">
        <input
          id="minutes"
          type="text"
          value={minutes}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          onKeyDown={handleMinutesKeyDown}
          placeholder="00"
          maxLength={2}
          className="w-14 px-2 py-2 text-sm text-center text-gray-600 border border-gray-300 rounded bg-bits-grey-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <label className="text-xs text-gray-500 text-center mt-1">
          Minutes
        </label>
      </div>

      {/* Display formatted time */}
      <div className="ml-2 text-sm text-gray-600 font-mono">
        {formatTime(hours, minutes)}
      </div>
    </div>
  );
};
